/**
 * Shared site navbar (converted from the Webflow fixed navbar).
 *
 * Includes the animated Lottie hamburger + fullscreen category menu, the
 * Lottie search toggle + expanding popup, and the cart button + right sidebar
 * drawer wired to the client cart. Sticky show/hide on scroll. All motion is
 * GSAP/Lottie; no Webflow JS.
 */
import type { ChangeEvent, FC, FormEvent, KeyboardEvent } from 'react'
import type { Category } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { gsap } from '@/lib/gsap'
import { useCart } from '@/lib/cart'
import { LottieIcon } from '@/components/animations/lottie-icon'

interface NavbarProps {
    categories: Category[]
}

export const Navbar: FC<NavbarProps> = ({ categories }) => {
    const router = useRouter()
    const cart = useCart()

    const [menuOpen, setMenuOpen] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    const fixedNavRef = useRef<HTMLDivElement>(null)
    const navMenuRef = useRef<HTMLElement>(null)
    const menuImageRef = useRef<HTMLDivElement>(null)
    const searchPopupRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const searchWrapRef = useRef<HTMLDivElement>(null)
    const menuFirstRun = useRef(true)

    /* Fullscreen menu open / close */
    useEffect(() => {
        const menu = navMenuRef.current
        if (!menu) return

        // Flip the inner .nav-link-container (it lives inside
        // .nav-link-container-perspective, which sets perspective: 1000px) so the
        // rotationX reads as a real 3D flip-up — matching the Webflow IX2 menu.
        const linkContainers = menu.querySelectorAll('.nav-link-container')
        const footer = menu.querySelector('.nav-footer-holder')
        const image = menuImageRef.current
        const isDesktop = window.matchMedia('(min-width: 768px)').matches

        if (menuFirstRun.current) {
            menuFirstRun.current = false
            gsap.set(menu, { display: 'none', height: 0 })
            gsap.set(linkContainers, { opacity: 0, rotationX: -90, y: 30 })
            if (footer) gsap.set(footer, { yPercent: 100 })
            if (image) gsap.set(image, { width: 0 })
            return
        }

        if (menuOpen) {
            document.body.style.overflow = 'hidden'
            gsap.set(menu, { display: 'flex' })
            const tl = gsap.timeline({ defaults: { ease: 'power1.inOut' } })
            tl.fromTo(menu, { height: 0 }, { height: '100vh', duration: 1.2 }, 0)
            if (footer) tl.fromTo(footer, { yPercent: 100 }, { yPercent: 0, duration: 0.8 }, 0)
            if (image) {
                tl.fromTo(image, { width: 0 }, { width: isDesktop ? '50vw' : '100vw', duration: 1 }, 0.5)
            }
            tl.fromTo(
                linkContainers,
                { opacity: 0, rotationX: -90, y: 30 },
                { opacity: 1, rotationX: 0, y: 0, duration: 0.8, stagger: 0.1 },
                0.3
            )
        } else {
            document.body.style.overflow = ''
            const tl = gsap.timeline({
                defaults: { ease: 'power1.inOut' },
                onComplete: () => gsap.set(menu, { display: 'none' })
            })
            tl.to(linkContainers, { opacity: 0, rotationX: -90, y: 30, duration: 0.6, stagger: 0.1 }, 0)
            if (image) tl.to(image, { width: 0, duration: 0.8 }, 0)
            if (footer) tl.to(footer, { yPercent: 100, duration: 0.8 }, 0)
            tl.to(menu, { height: 0, duration: 1 }, 0)
        }
    }, [menuOpen])

    /* Search popup open / close */
    useEffect(() => {
        const popup = searchPopupRef.current
        if (!popup) return

        const field = popup.querySelector('.navbar-search-icon-holder')
        const isDesktop = window.matchMedia('(min-width: 768px)').matches

        if (searchOpen) {
            const tl = gsap.timeline()
            tl.to(popup, {
                width: isDesktop ? 420 : 240,
                backgroundColor: 'rgba(255,255,255,1)',
                borderColor: 'rgba(235,235,235,1)',
                duration: 0.45,
                ease: 'power3.out'
            })
            // Field contents glide in with the box instead of popping into view.
            if (field) {
                tl.fromTo(
                    field,
                    { opacity: 0, x: 16 },
                    { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out' },
                    0.12
                )
            }
            // Focus once the field is actually visible.
            tl.add(() => searchInputRef.current?.focus(), 0.2)
        } else {
            const tl = gsap.timeline()
            if (field) tl.to(field, { opacity: 0, x: 16, duration: 0.2, ease: 'power1.in' }, 0)
            tl.to(
                popup,
                {
                    width: 0,
                    backgroundColor: 'rgba(255,255,255,0)',
                    borderColor: 'rgba(255,255,255,0)',
                    duration: 0.35,
                    ease: 'power3.inOut'
                },
                0.05
            )
        }
    }, [searchOpen])

    /* Dismiss the search field when clicking anywhere outside it */
    useEffect(() => {
        if (!searchOpen) return

        const onPointerDown = (event: MouseEvent) => {
            if (!searchWrapRef.current?.contains(event.target as Node)) {
                setSearchOpen(false)
            }
        }

        document.addEventListener('mousedown', onPointerDown)
        return () => document.removeEventListener('mousedown', onPointerDown)
    }, [searchOpen])

    /* Sticky show on scroll up, hide on scroll down */
    useEffect(() => {
        const bar = fixedNavRef.current
        if (!bar) return

        let lastY = window.scrollY

        const onScroll = () => {
            const y = window.scrollY
            if (y < 100 || y < lastY) {
                gsap.to(bar, { opacity: 1, duration: 0.3, overwrite: 'auto' })
                bar.style.pointerEvents = 'auto'
            } else if (y > lastY) {
                gsap.to(bar, { opacity: 0, duration: 0.3, overwrite: 'auto' })
                bar.style.pointerEvents = 'none'
            }
            lastY = y
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    /* Close everything on route change + Escape */
    useEffect(() => {
        const close = () => {
            setMenuOpen(false)
            setSearchOpen(false)
            cart.closeCart()
        }

        router.events.on('routeChangeStart', close)

        const onKey = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') close()
        }
        window.addEventListener('keydown', onKey)

        return () => {
            router.events.off('routeChangeStart', close)
            window.removeEventListener('keydown', onKey)
        }
    }, [router.events, cart])

    const onSearchSubmit = (event: FormEvent) => {
        event.preventDefault()
        const query = searchValue.trim()
        setSearchOpen(false)
        router.push(query ? `/search?query=${encodeURIComponent(query)}` : '/search')
    }

    const onKeyActivate = (handler: () => void) => (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handler()
        }
    }

    return (
        <div ref={fixedNavRef} className='fixed-navbar'>
            <div className='navbar w-nav' role='banner' data-collapse='all'>
                <div className='container nav-container'>
                    <div className='nav-menu-container'>
                        <div className='navbar-menu-holder'>
                            <Link href='/' className='brand w-nav-brand'>
                                <img src='/images/Milano-Logo-Black.svg' loading='lazy' alt='Milano' className='brand-image-black' />
                                <img src='/images/Milano-Logo-White.svg' loading='lazy' alt='Milano' className='brand-image-white' />
                            </Link>

                            <div
                                className='menu-button w-nav-button'
                                role='button'
                                tabIndex={0}
                                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                                aria-expanded={menuOpen}
                                onClick={() => setMenuOpen((value) => !value)}
                                onKeyDown={onKeyActivate(() => setMenuOpen((value) => !value))}
                            >
                                <LottieIcon
                                    src='/lottie/menuV3.json'
                                    open={menuOpen}
                                    duration={menuOpen ? 1.3 : 1}
                                    className='menu-hamburger-icon'
                                    aria-hidden='true'
                                />
                            </div>
                        </div>

                        <div className='navbar-icons-holder'>
                            <div ref={searchWrapRef} className='navbar-icons-container'>
                                <LottieIcon
                                    src='/lottie/searchToX.json'
                                    open={searchOpen}
                                    duration={0.6}
                                    className='navbar-search-icon-lottie'
                                    role='button'
                                    tabIndex={0}
                                    aria-label={searchOpen ? 'Close search' : 'Open search'}
                                    aria-expanded={searchOpen}
                                    onClick={() => setSearchOpen((value) => !value)}
                                    onKeyDown={onKeyActivate(() => setSearchOpen((value) => !value))}
                                />

                                <div ref={searchPopupRef} className='navbar-search-popup'>
                                    <div className='navbar-search-icon-holder'>
                                        <form onSubmit={onSearchSubmit} className='search w-form' role='search'>
                                            <input type='submit' className='search-button w-button' value='' aria-label='Search' />
                                            <input
                                                ref={searchInputRef}
                                                className='search-input w-input'
                                                maxLength={256}
                                                name='query'
                                                placeholder='Search…'
                                                type='search'
                                                value={searchValue}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value)}
                                            />
                                        </form>
                                    </div>
                                </div>
                            </div>

                            <div className='navbar-icons-container'>
                                <div className='w-commerce-commercecartwrapper'>
                                    <a
                                        href='#'
                                        aria-haspopup='dialog'
                                        aria-label='Open cart'
                                        role='button'
                                        className='w-commerce-commercecartopenlink cart-button w-inline-block'
                                        onClick={(event) => {
                                            event.preventDefault()
                                            cart.openCart()
                                        }}
                                    >
                                        <img src='/images/Shop-Icon.svg' loading='lazy' alt='' className='cart-image' />
                                        <div className='w-commerce-commercecartopenlinkcount cart-quantity'>
                                            {cart.hydrated ? cart.count : 0}
                                        </div>
                                    </a>
                                </div>
                            </div>
                        </div>

                        <nav ref={navMenuRef} role='navigation' className='nav-menu w-nav-menu'>
                            <div className='nav-manu-mask'>
                                <div className='container menu-holder'>
                                    <div className='nav-link-holder'>
                                        <div className='w-dyn-list'>
                                            <div role='list' className='w-dyn-items'>
                                                {categories.map((category, index) => (
                                                    <div key={category.slug} role='listitem' className='w-dyn-item'>
                                                        <Link
                                                            href={`/category/${category.slug}`}
                                                            className='nav-link w-inline-block'
                                                            onClick={() => setMenuOpen(false)}
                                                        >
                                                            <div className='nav-link-container-perspective'>
                                                                <div className='nav-link-container'>
                                                                    <div className='number-holder'>
                                                                        <div className='overline-l'>0</div>
                                                                        <div className='overline-l'>{index + 1}</div>
                                                                        <div className='overline-l'>.</div>
                                                                    </div>
                                                                    <div className='link-block-menu'>{category.name}</div>
                                                                </div>
                                                            </div>
                                                            <div className='nav-menu-image-holder nav-link-image'>
                                                                <img loading='lazy' alt='' src={category.image} className='nav-menu-image' />
                                                            </div>
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className='nav-footer-holder'>
                                        <div className='nav-menu-footer-line' />
                                        <div className='nav-menu-links-container'>
                                            <div className='links-holder'>
                                                <a href='mailto:hello@milano.clothes' className='nav-menu-footer-links'>
                                                    <span className='accent-text-color'>E: </span>hello@milano.clothes
                                                </a>
                                                <a href='tel:+38304912345' className='nav-menu-footer-links'>
                                                    <span className='accent-text-color'>T: </span>+383 (0) 49 123-456
                                                </a>
                                            </div>
                                            <div className='social-media-container'>
                                                <a href='https://youtube.com' target='_blank' rel='noreferrer' className='social-media-holder w-inline-block'>
                                                    <img src='/images/youtube----Gold.svg' loading='lazy' alt='YouTube' className='social-media-icon' />
                                                </a>
                                                <a href='https://facebook.com' target='_blank' rel='noreferrer' className='social-media-holder w-inline-block'>
                                                    <img src='/images/facebook---Gold.svg' loading='lazy' alt='Facebook' className='social-media-icon' />
                                                </a>
                                                <a href='https://twitter.com' target='_blank' rel='noreferrer' className='social-media-holder w-inline-block'>
                                                    <img src='/images/twitter----Gold.svg' loading='lazy' alt='Twitter' className='social-media-icon' />
                                                </a>
                                                <a href='https://instagram.com' target='_blank' rel='noreferrer' className='social-media-holder w-inline-block'>
                                                    <img src='/images/instagram----Gold.svg' loading='lazy' alt='Instagram' className='social-media-icon' />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div ref={menuImageRef} className='nav-menu-image-holder'>
                                    <img
                                        src='/images/CTA-Image-04.jpg'
                                        loading='lazy'
                                        sizes='100vw'
                                        srcSet='/images/CTA-Image-04-p-500.jpg 500w, /images/CTA-Image-04-p-800.jpg 800w, /images/CTA-Image-04-p-1080.jpg 1080w, /images/CTA-Image-04.jpg 1300w'
                                        alt=''
                                        className='nav-menu-image'
                                    />
                                    <div className='nav-menu-image-mask' />
                                </div>
                            </div>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    )
}
